import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
} from "@angular/core";
import { FormGroup, FormsModule, ReactiveFormsModule } from "@angular/forms";
import { UntilDestroy } from "@ngneat/until-destroy";
import { ProgramDimensionDto } from "../../../../core/models/program/programDimensionDto";
import { AddProgramDto } from "../../../../core/models/program/addProgramDto";
import { DropDownDto } from "../../../../core/models/program/dropdownDto";
import { ProgramBCDto } from "../../../../core/models/program/programBCListDto";
import { ValidateLogin } from "../../../../core/models/validate-login.dto";
import { CommonModule } from "@angular/common";
import { UserManagementService } from "../../../../core/services/user-management.service";
import { SelectModule } from "primeng/select";
import { ProfileFormService } from "../../../../core/services/profile-form.service";
import { GenericResponseDto } from "../../../../core/models/generic-response.dto";
import { ButtonModule } from "primeng/button";
import { CardModule } from "primeng/card";
import { InputTextModule } from "primeng/inputtext";
import { ToggleSwitchModule } from "primeng/toggleswitch";

@UntilDestroy()
@Component({
  selector: "jni-program-header",
  templateUrl: "./program-header.component.html",
  styleUrls: ["./program-header.component.scss"],
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    SelectModule,
    ButtonModule,
    CardModule,
    InputTextModule,
    ToggleSwitchModule,
  ],
})
export class ProgramHeaderComponent
  implements OnInit, OnChanges, AfterViewInit
{
  threePl1!: number;
  facilitator!: number;
  dimensionD365Val = "";
  webPlatform!: number;
  bcVal = "";
  loggedInUserIsBC = false;

  formErrors: Record<string, string> = {
    name: "",
    brandingConsultantD365: "",
  };

  @Input() parent!: FormGroup;
  @Input() isReadOnly = false;
  @Input() validateLogin = {} as ValidateLogin;
  @Input() platformList: DropDownDto[] = [];
  @Input() facilitatorList: DropDownDto[] = [];
  @Input() threePLList: DropDownDto[] = [];
  @Input() pgmDimensionList: ProgramDimensionDto[] = [];
  @Input() bcList: ProgramBCDto[] = [];
  @Input() programId = 0;
  @Input() programData = {} as AddProgramDto;
  @Input() isAdminCheck = false;
  @Input() isEditDimension = false;
  @Input() mode = "";
  // @Input() set data(value: any) {
  //   if (Object.entries(value).length) {
  //     this.programData = value;
  //     this.ngOnInit();
  //   } else {
  //     this.programData = {} as AddProgramDto
  //   }
  // }

  @Output() emitOnApprove = new EventEmitter<boolean>();
  @Output() smallLoaderEvent = new EventEmitter<boolean>();
  @Output() emitFormGroup: EventEmitter<FormGroup> =
    new EventEmitter<FormGroup>();
  @Output() emitDimensionChange: EventEmitter<string> =
    new EventEmitter<string>();

  constructor(
    private _userService: UserManagementService,
    private _profileFormService: ProfileFormService
  ) {}

  ngOnInit() {
    this.getUserProfile();
  }

  ngAfterViewInit(): void {
    if (this.isReadOnly) {
      this.mapFormfeilds();
      this.setDropdownValues();
    }
  }

  validationMessages: Record<string, Record<string, string>> = {
    name: {
      required: "Program name is required.",
    },
    brandingConsultantD365: {
      required: "BC is required.",
    },
  };

  logValidationErrors(group: FormGroup = this.parent): void {
    Object.keys(group.controls).forEach((key: string) => {
      const abstractControl = group.get(key);
      if (abstractControl instanceof FormGroup) {
        this.logValidationErrors(abstractControl);
      } else {
        this.formErrors[key] = "";
        if (
          abstractControl &&
          !abstractControl.valid &&
          (abstractControl.touched || abstractControl.dirty)
        ) {
          const messages = this.validationMessages[key];
          for (const errorKey in abstractControl.errors) {
            if (errorKey && messages) {
              this.formErrors[key] += messages[errorKey] + " ";
            }
          }
        }
      }
    });
  }

  ngOnChanges() {
    if (this.isReadOnly) {
      this.mapFormfeilds();
      this.setDropdownValues();
    }
  }

  getUserProfile() {
    this._userService
      .validateLogin()
      .pipe()
      .subscribe((res: GenericResponseDto<ValidateLogin>) => {
        this.getUserById(res?.data?.userId);
      });
  }

  getUserById(userId: number) {
    this._profileFormService.getUsersById(userId).subscribe({
      next: res => {
        if (res?.data) {
          if (
            res?.data?.title?.toLowerCase()?.includes("branding consultant")
          ) {
            this.loggedInUserIsBC = true;
            if (!this.isReadOnly) {
              this.bcVal = res?.data?.personnelNumber || "";
            }
          }
        }
      },
    });
  }

  setDropdownValues() {
    this.bcVal = this.programData.brandingConsultantD365 ?? "";
    this.webPlatform = this.programData.webPlatId ?? 0;
    this.threePl1 = this.programData.threePL1Id ?? 0;
    this.facilitator = this.programData.inStoreCCProvider ?? 0;
    this.dimensionD365Val = this.programData.dimensionD365 ?? "";
  }

  onChange() {
    this.emitFormGroup.emit(this.parent);
  }

  selectionChange() {
    this.emitFormGroup.emit(this.parent);
  }

  onValueChange() {
    this.emitFormGroup.emit(this.parent);
  }

  public mapFormfeilds() {
    this.parent.patchValue({
      id: this.programData.id,
      name: this.programData.name,
      office: this.programData.office,
      brandingConsultantD365: this.programData.brandingConsultantD365,
      description: this.programData.description,
      ecomOnboard: this.programData.ecomOnboard,
      inStoreCC: this.programData.inStoreCC,
      inStoreCCProvider: this.programData.inStoreCCProvider,
      intl: this.programData.intl,
      usesWebsite: this.programData.usesWebsite,
      uses3PL: this.programData.uses3PL,
      dimensionD365: this.programData.dimensionD365,
      clientOwnInv: this.programData.clientOwnInv,
      nadelOwnInv: this.programData.nadelOwnInv,
      webPlatId: this.programData.webPlatId,
      avaTaxIntegration: this.programData.avaTaxIntegration,
      threePLOther: this.programData.threePLOther,
      threePL1Id: this.programData.threePL1Id,
      micro: this.programData.micro,
      website1: this.programData.website1,
      website2: this.programData.website2,
      isApproved: this.programData.isApproved,
      isDisabled: this.programData.isDisabled,
      isClientLock: this.programData.isClientLock,
    });

    if (this.parent.controls["webPlatId"].value == 0) {
      this.parent.controls["webPlatId"].setValue("");
    }

    if (this.parent.controls["threePL1Id"].value == 0) {
      this.parent.controls["threePL1Id"].setValue("");
    }

    if (this.parent.controls["inStoreCCProvider"].value == 0) {
      this.parent.controls["inStoreCCProvider"].setValue("");
    }

    this.parent.disable();
    if (this.isAdminCheck && !this.isEditDimension) {
      this.parent.get("dimensionD365")?.enable();
    }
  }

  onApprove() {
    const isApproved = this.parent.controls["isApproved"].value ?? false;
    this.emitOnApprove.emit(isApproved);
  }
}
